import { useEffect, useState } from "react";
import api from "@/lib/api";
import { SectionCard } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const EMPTY = {
  billing_type: "",             // "individual" | "company"
  company_kind: "",             // "sahis_sirketi" | "limited" | "anonim" | "diger"
  first_name: "", last_name: "",
  full_name: "",                // şirket yetkili ad soyad
  legal_name: "",
  tax_office: "",
  tax_number: "",
  national_id: "",
  billing_address: "", billing_city: "", billing_district: "",
};

const COMPANY_KIND_OPTS = [
  { code: "sahis_sirketi", label: "Şahıs Şirketi" },
  { code: "limited",       label: "Limited Şirket" },
  { code: "anonim",        label: "Anonim Şirket" },
  { code: "diger",         label: "Diğer" },
];

/**
 * Ayarlar → Fatura Bilgileri kartı.  Kayıt sırasında toplanan yeni
 * şemayı düzenlemeye izin verir:
 *   - Şahıs: ad, soyad, T.C. kimlik numarası
 *   - Şirket: alt tür (Şahıs Şirketi / Limited / Anonim / Diğer),
 *     firma ünvanı, yetkili ad soyad, TCKN veya VKN, vergi dairesi,
 *     fatura adresi (il/ilçe)
 * Server-side de aynı doğrulama vardır.
 */
export default function BillingCard() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/billing");
        setForm({
          ...EMPTY,
          ...data,
          billing_type: data.billing_type || "",
          company_kind: data.company_kind || "",
        });
      } catch {
        toast.error("Fatura bilgileri yüklenemedi.");
      } finally { setLoading(false); }
    })();
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.billing_type) {
      toast.error("Lütfen hesap türünü seçin.");
      return;
    }
    if (form.billing_type === "company" && !form.company_kind) {
      toast.error("Lütfen şirket türünü seçin.");
      return;
    }
    setSaving(true);
    try {
      const isIndividual = form.billing_type === "individual";
      const isSahisSirketi =
        form.billing_type === "company" && form.company_kind === "sahis_sirketi";
      const payload = {
        billing_type: form.billing_type,
        company_kind: form.billing_type === "company" ? form.company_kind : null,
        first_name: isIndividual ? (form.first_name || "").trim() : "",
        last_name:  isIndividual ? (form.last_name || "").trim() : "",
        full_name:  form.billing_type === "company" ? (form.full_name || "").trim() : "",
        legal_name: form.billing_type === "company" ? (form.legal_name || "").trim() : "",
        tax_office: form.billing_type === "company" ? (form.tax_office || "").trim() : "",
        billing_address: form.billing_type === "company" ? (form.billing_address || "").trim() : "",
        billing_city:    form.billing_type === "company" ? (form.billing_city || "").trim() : "",
        billing_district:form.billing_type === "company" ? (form.billing_district || "").trim() : "",
        national_id: (isIndividual || isSahisSirketi)
          ? (form.national_id || "").replace(/\D/g, "")
          : "",
        tax_number: (form.billing_type === "company" && !isSahisSirketi)
          ? (form.tax_number || "").replace(/\D/g, "")
          : "",
      };
      const { data } = await api.put("/settings/billing", payload);
      setForm({
        ...EMPTY,
        ...data,
        billing_type: data.billing_type || "",
        company_kind: data.company_kind || "",
      });
      toast.success("Fatura bilgileri güncellendi.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kayıt başarısız.");
    } finally { setSaving(false); }
  };

  const isIndividual = form.billing_type === "individual";
  const isCompany = form.billing_type === "company";
  const isSahisSirketi = isCompany && form.company_kind === "sahis_sirketi";

  return (
    <SectionCard title="Fatura Bilgileri" testId="billing-card">
      <form onSubmit={submit} className="p-5 space-y-4" data-testid="billing-form">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor…
          </div>
        ) : (
          <>
            <div>
              <Label className="text-xs font-medium text-slate-700">Hesap Türü</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                {[
                  { code: "individual", label: "Şahıs" },
                  { code: "company",    label: "Şirket" },
                ].map((opt) => (
                  <button
                    key={opt.code}
                    type="button"
                    onClick={() => setForm((f) => ({
                      ...f,
                      billing_type: opt.code,
                      // türü değiştirdiğimizde alt tür sıfırlanır
                      company_kind: opt.code === "company" ? f.company_kind : "",
                    }))}
                    className={
                      "h-10 rounded-lg border text-sm font-medium transition-colors " +
                      (form.billing_type === opt.code
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-400")
                    }
                    data-testid={`billing-type-${opt.code}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Şahıs alanları */}
            {isIndividual && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <TextField label="Ad" value={form.first_name}
                    onChange={(v) => setField("first_name", v)}
                    testId="billing-first-name" required />
                  <TextField label="Soyad" value={form.last_name}
                    onChange={(v) => setField("last_name", v)}
                    testId="billing-last-name" required />
                </div>
                <TextField label="T.C. Kimlik Numarası" value={form.national_id}
                  onChange={(v) => setField("national_id", v.replace(/\D/g, "").slice(0, 11))}
                  inputMode="numeric" maxLength={11}
                  testId="billing-national-id" required />
              </>
            )}

            {/* Şirket alt türü */}
            {isCompany && (
              <div>
                <Label className="text-xs font-medium text-slate-700">Şirket Türü</Label>
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  {COMPANY_KIND_OPTS.map((opt) => (
                    <button
                      key={opt.code}
                      type="button"
                      onClick={() => setField("company_kind", opt.code)}
                      className={
                        "h-10 rounded-lg border text-sm font-medium transition-colors " +
                        (form.company_kind === opt.code
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-400")
                      }
                      data-testid={`billing-company-kind-${opt.code}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Şirket alanları — alt tür seçildikten sonra göster */}
            {isCompany && form.company_kind && (
              <>
                <TextField label="Firma / Ticari Ünvan" value={form.legal_name}
                  onChange={(v) => setField("legal_name", v)}
                  testId="billing-legal-name" required />
                <TextField label="Yetkili Ad Soyad" value={form.full_name}
                  onChange={(v) => setField("full_name", v)}
                  testId="billing-authorized-person" required />
                {isSahisSirketi ? (
                  <TextField label="T.C. Kimlik Numarası" value={form.national_id}
                    onChange={(v) => setField("national_id", v.replace(/\D/g, "").slice(0, 11))}
                    inputMode="numeric" maxLength={11}
                    testId="billing-national-id" required />
                ) : (
                  <TextField label="Vergi Numarası" value={form.tax_number}
                    onChange={(v) => setField("tax_number", v.replace(/\D/g, "").slice(0, 10))}
                    inputMode="numeric" maxLength={10}
                    testId="billing-tax-number" required />
                )}
                <TextField label="Vergi Dairesi" value={form.tax_office}
                  onChange={(v) => setField("tax_office", v)}
                  testId="billing-tax-office" required />
                <TextField label="Fatura Adresi" value={form.billing_address}
                  onChange={(v) => setField("billing_address", v)}
                  testId="billing-address" required />
                <div className="grid grid-cols-2 gap-3">
                  <TextField label="İl" value={form.billing_city}
                    onChange={(v) => setField("billing_city", v)}
                    testId="billing-city" required />
                  <TextField label="İlçe" value={form.billing_district}
                    onChange={(v) => setField("billing_district", v)}
                    testId="billing-district" required />
                </div>
              </>
            )}

            <div className="pt-2">
              <Button type="submit"
                      disabled={saving || !form.billing_type || (isCompany && !form.company_kind)}
                      className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50"
                      data-testid="billing-save-button">
                {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                Bilgileri Kaydet
              </Button>
            </div>
          </>
        )}
      </form>
    </SectionCard>
  );
}

function TextField({ label, value, onChange, testId, required, inputMode, maxLength }) {
  return (
    <div>
      <Label className="text-xs font-medium text-slate-700">{label}</Label>
      <Input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 h-10 rounded-lg border-slate-200"
        required={required}
        inputMode={inputMode}
        maxLength={maxLength}
        data-testid={testId}
      />
    </div>
  );
}
